import { t } from "@travily/i18n";
import type Image from "@travily/types/shared/Image";
import { Image as PreviewImage } from "@ui/components/Image";
import { formatPrice } from "@ui/lib/format-price";
import type { ReactNode } from "react";

/**
 * The card payload every editor page ships alongside its section list —
 * mirrors `Catalog::Serializers::…::EditorProps::BASE_PROPS`.
 */
export interface EditorCardData {
  delivery_kind?: string | null;
  delivery_name?: string | null;
  description_preview?: string | null;
  has_delivery: boolean;
  listing_title?: string | null;
  photo_count: number;
  photo_previews: Image[];
  price_cents?: number | null;
  price_currency?: string | null;
  price_type: string;
  tagline?: string | null;
}

export type EditorCardRenderers = Record<string, (data: EditorCardData) => ReactNode>;

function preview(text: string | null | undefined, clamp?: "line-clamp-2" | "line-clamp-5"): ReactNode {
  if (!text) return null;
  return <p className={`mt-0.5 text-neutral-500 text-sm ${clamp ?? ""}`}>{text}</p>;
}

/** Middle photo sits upright and on top; neighbours lean away like a hand of snapshots. */
const FAN_TILTS = ["-rotate-6 translate-y-0.5", "rotate-2 z-10 -translate-y-0.5", "rotate-6 translate-y-0.5"];

function PhotosCard({ data }: { data: EditorCardData }) {
  if (data.photo_previews.length === 0) {
    return <p className="mt-0.5 text-neutral-500 text-sm">{t("web.listings.fields.photos.add_photos")}</p>;
  }

  return (
    <div className="mt-3 mb-1 flex items-center gap-5">
      <div className="flex -space-x-3 pl-1">
        {data.photo_previews.map((image, index) => (
          <div
            key={image.src}
            className={`size-14 shrink-0 overflow-hidden rounded-xl shadow-chip ring-2 ring-white ${FAN_TILTS[index] ?? ""}`}
          >
            <PreviewImage className="size-full object-cover" image={image} sizes="56px" />
          </div>
        ))}
      </div>
      <span className="text-neutral-500 text-sm">{t("common.photo_count", { count: data.photo_count })}</span>
    </div>
  );
}

function contentSummary(data: EditorCardData): string | null {
  if (!data.has_delivery) return null;
  const itemCount = t("web.listings.fields.content.item_count", { count: 1 });
  return data.delivery_name ? `${itemCount} · ${data.delivery_name}` : itemCount;
}

function fileExtension(name: string | null | undefined): string | null {
  const extension = name?.match(/\.(\w{1,5})$/)?.[1];
  return extension ? extension.toUpperCase() : null;
}

/**
 * A file delivery renders as a document snapshot in the photos-fan language:
 * a tilted white page with skeleton text lines and the file extension. Real
 * first-page thumbnails would need poppler/ffmpeg in the production image —
 * not worth it for a 56px thumb, and zip/video deliveries could never preview.
 */
function ContentCard({ data }: { data: EditorCardData }) {
  if (!data.has_delivery) return null;
  if (data.delivery_kind !== "file") return preview(contentSummary(data), "line-clamp-2");

  return (
    <div className="mt-3 mb-1 flex items-center gap-5">
      <div className="ml-1 flex h-14 w-11 shrink-0 -rotate-3 flex-col justify-between rounded-lg bg-white p-2 shadow-chip ring-1 ring-neutral-100">
        <div className="flex flex-col gap-1">
          <div className="h-0.5 w-full rounded-full bg-neutral-200" />
          <div className="h-0.5 w-full rounded-full bg-neutral-200" />
          <div className="h-0.5 w-2/3 rounded-full bg-neutral-200" />
        </div>
        <span className="font-semibold text-[9px] text-neutral-500 leading-none tracking-wide">
          {fileExtension(data.delivery_name)}
        </span>
      </div>
      <span className="min-w-0 truncate text-neutral-500 text-sm">{contentSummary(data)}</span>
    </div>
  );
}

function priceTypeLabel(data: EditorCardData): string {
  return data.price_type === "paid"
    ? t("web.listings.fields.price_type.paid_label")
    : t("web.listings.fields.price_type.free_label");
}

function PriceAmountCard({ data }: { data: EditorCardData }) {
  if (data.price_cents == null || !data.price_currency) return null;
  return (
    <p className="mt-0.5 text-neutral-500 text-sm tabular-nums">{formatPrice(data.price_cents, data.price_currency)}</p>
  );
}

/**
 * How each listing editor section presents itself in the section nav. Keyed by
 * the field-registry section key; an unregistered key degrades to a bare label,
 * so the server can grow the section set ahead of this map. Editors for other
 * entities supply their own map to `EditorSectionNav` / `buildEditorSections`.
 */
export const listingSectionCards: EditorCardRenderers = {
  // Title and tagline are length-validated (50/160 chars), so they render in
  // full; only the free-form description preview needs a clamp.
  title: (data) =>
    data.listing_title ? <p className="mt-0.5 text-base text-neutral-500">{data.listing_title}</p> : null,
  tagline: (data) => preview(data.tagline),
  description: (data) => preview(data.description_preview, "line-clamp-5"),
  photos: (data) => <PhotosCard data={data} />,
  content: (data) => <ContentCard data={data} />,
  price_type: (data) => preview(priceTypeLabel(data), "line-clamp-2"),
  price_amount: (data) => <PriceAmountCard data={data} />,
};
